import { Paper, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import { FAILURE_REASON_LABEL } from 'common/components/PrintResultSummary';
import { formatQty } from '../utils/bulkLotNdcStatus';

const INSUFFICIENT_LOT_QUANTITY_IN_BATCH = 'INSUFFICIENT_LOT_QUANTITY_IN_BATCH';

// The refusals that mean the lot's stock moved after the review read it.
const STOCK_REFUSAL_REASONS = new Set([
  INSUFFICIENT_LOT_QUANTITY_IN_BATCH,
  'LOT_NOT_AVAILABLE',
  'MANUAL_LOT_HAS_AVAILABLE_STOCK',
]);

export const isRefusedForStock = (row) => !row.success && STOCK_REFUSAL_REASONS.has(row.reason);

/**
 * POM-6535 — the one refusal worded per row: which lot ran short, where, and by how much, in the
 * words of the ticket (AC 5). Every other reason reads its fixed label.
 */
export const describeRefusalReason = ({ row, facilityName }) => {
  if (row.reason === INSUFFICIENT_LOT_QUANTITY_IN_BATCH && row.lotNumber) {
    return `Insufficient quantity in lot ${row.lotNumber} at ${
      facilityName || 'this facility'
    } (short ${formatQty(row.shortfallQty)})`;
  }

  return FAILURE_REASON_LABEL[row.reason] ?? row.reason ?? 'Refused';
};

/**
 * POM-6010 / POM-6121 — the rows a commit refused.
 *
 * Extracted from BulkLotNdcReviewDialog when POM-6121 gave the commit a new way to refuse one row
 * while its siblings succeed: a hand-recorded claim against an NDC core cannot resolve is rejected
 * per row, so this panel is where the operator finds out which one and why.
 *
 * A reason with no entry in FAILURE_REASON_LABEL renders as its raw code, which is the regression
 * QA re-opened POM-5933 for — so every new core refusal reason needs a label there.
 */
const BulkLotNdcRefusedPanel = ({ refused, items }) => {
  if (refused.length === 0) return null;

  const itemById = new Map(items.map((item) => [item.requestLineItemId, item]));

  return (
    <Paper variant='outlined' sx={{ mt: 2, p: 2 }}>
      <Typography variant='subtitle2' gutterBottom>
        {`${refused.length} item(s) kept their original lot`}
      </Typography>
      {refused.map((row) => {
        const item = itemById.get(row.requestLineItemId);
        // The Rx number is what the operator finds the row by in the grid; the line-item id only
        // when the review no longer holds the row.
        const rowName = item?.rxNumber ? `Rx ${item.rxNumber}` : row.requestLineItemId;

        return (
          <Typography key={row.requestLineItemId} variant='body2' color='text.secondary'>
            {`${rowName} — ${describeRefusalReason({
              row,
              facilityName: item?.fulfillingPharmacyName,
            })}`}
          </Typography>
        );
      })}
    </Paper>
  );
};

BulkLotNdcRefusedPanel.propTypes = {
  refused: PropTypes.arrayOf(
    PropTypes.shape({
      requestLineItemId: PropTypes.string,
      reason: PropTypes.string,
      lotNumber: PropTypes.string,
      shortfallQty: PropTypes.number,
    })
  ).isRequired,
  // The review's rows, for each refused row's Rx number and facility.
  items: PropTypes.arrayOf(
    PropTypes.shape({
      requestLineItemId: PropTypes.string,
      rxNumber: PropTypes.string,
      fulfillingPharmacyName: PropTypes.string,
    })
  ),
};

BulkLotNdcRefusedPanel.defaultProps = {
  items: [],
};

export default BulkLotNdcRefusedPanel;
